import React, { useState, useEffect } from 'react';
import { getViewStats } from '../lib/supabase';
import { useProfile } from '../contexts/ProfileContext';
import { FiEye, FiTrendingUp, FiUsers, FiGlobe, FiDollarSign, FiAward } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const ProfileStats = ({ profileId }) => {
  const [stats, setStats] = useState({
    totalViews: 0,
    weeklyViews: 0,
    uniqueVisitors: 0,
    growth: 0,
    totalTips: 0,
    badges: 0,
  });
  const [loading, setLoading] = useState(true);
  const { profile } = useProfile();

  useEffect(() => {
    loadStats();
  }, [profileId, profile]);

  const loadStats = async () => {
    try {
      const { data: views } = await getViewStats(profileId);
      if (views) {
        const totalViews = views.length;
        const now = new Date();
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const weeklyViews = views.filter(
          view => new Date(view.viewed_at) >= weekAgo
        ).length;
        const uniqueVisitors = new Set(
          views.filter(view => view.viewer_wallet).map(view => view.viewer_wallet)
        ).size;

        // Calculate growth (simplified)
        const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
        const previousWeekViews = views.filter(
          view => new Date(view.viewed_at) >= twoWeeksAgo && new Date(view.viewed_at) < weekAgo
        ).length;
        const growth = previousWeekViews > 0
            ? Math.round(((weeklyViews - previousWeekViews) / previousWeekViews) * 100)
            : 0;
            
        // Mock additional stats for Phase 2
        const totalTips = Math.floor(Math.random() * 50) * 0.1; // Mock tips
        const badges = profile ? 4 + Math.floor(Math.random() * 3) : 0; // Mock badges

        setStats({
          totalViews,
          weeklyViews,
          uniqueVisitors,
          growth,
          totalTips,
          badges,
        });
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { name: 'Total Views', value: stats.totalViews, icon: FiEye, color: 'text-blue-600', bg: 'bg-blue-100' },
    { name: 'This Week', value: stats.weeklyViews, icon: FiTrendingUp, color: 'text-green-600', bg: 'bg-green-100', change: stats.growth },
    { name: 'Unique Visitors', value: stats.uniqueVisitors, icon: FiUsers, color: 'text-purple-600', bg: 'bg-purple-100' },
    { name: 'Tips Earned', value: `${stats.totalTips.toFixed(2)} ETH`, icon: FiDollarSign, color: 'text-yellow-600', bg: 'bg-yellow-100' },
    { name: 'Badges', value: stats.badges, icon: FiAward, color: 'text-indigo-600', bg: 'bg-indigo-100' },
    { name: 'Profile Status', value: 'Live', icon: FiGlobe, color: 'text-green-600', bg: 'bg-green-100' },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="glass-dark p-6 rounded-xl border border-dark-700 animate-pulse">
            <div className="h-4 bg-dark-700 rounded w-3/4 mb-4"></div>
            <div className="h-8 bg-dark-700 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {statCards.map((stat, index) => (
        <div key={index} className="glass-dark p-6 rounded-xl border border-dark-700 hover:border-lime-500/50 transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-dark-400 mb-1">
                {stat.name}
              </p>
              <p className="text-2xl font-bold text-white">
                {typeof stat.value === 'number' && stat.name !== 'Tips Earned' ? stat.value.toLocaleString() : stat.value}
              </p>
              {stat.change !== undefined && (
                <p className={`text-sm mt-1 ${stat.change >= 0 ? 'text-lime-400' : 'text-red-400'}`}>
                  {stat.change >= 0 ? '+' : ''}{stat.change}%
                </p>
              )}
            </div>
            <div className="w-12 h-12 bg-lime-500/20 rounded-lg flex items-center justify-center border border-lime-500/50">
              <SafeIcon icon={stat.icon} className="text-lime-400 text-xl" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProfileStats;